import React from 'react'
import { 
  Add,
  Edit,
  Delete,
  Info,
  PlayCircleOutline
} from '@mui/icons-material'
import { CustomDefinition } from '../../../interfaces/db'
import { 
  formatMetricSource, 
  formatMetricType, 
  isDefinitionValid
} from '../utils'
import { Typography } from '../../../interfaces/typography'
import { Tooltip, PrimaryActionButton } from '../../../utils/CommonUIComponents'
import { EmptyState } from './EmptyState'

interface MetricsListProps {
  definitions: CustomDefinition[]
  hasChanges: boolean
  addNewDefinition: () => void
  setEditingDef: (def: CustomDefinition) => void
  handleDelete: (def: CustomDefinition) => void
  handleSave: (definitions: CustomDefinition[]) => void
  handleUndoChanges: () => void
  onTestMetric: (def: CustomDefinition) => void
}

export function MetricsList({ 
  definitions, 
  hasChanges, 
  addNewDefinition,
  setEditingDef,
  handleDelete,
  handleSave,
  handleUndoChanges,
  onTestMetric
}: MetricsListProps) {
  // Show empty state if no definitions
  if (definitions.length === 0 && !hasChanges) {
    return (
      <div>
        <EmptyState onAddMetric={addNewDefinition} />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <Typography variant="h2" color="#1F2937" fontWeight={600}>
              Custom Metrics
            </Typography>
            <Typography variant="mediumParagraph" color="#6B7280" className="mt-1">
              Create and manage your custom scoring metrics
            </Typography>
          </div>
          <PrimaryActionButton
            icon={<Add sx={{ fontSize: 18 }} />}
            onClick={addNewDefinition}
            className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 focus:ring-blue-500 rounded-lg"
          >
            Add New Metric
          </PrimaryActionButton>
        </div>
      </div>

      {/* Metrics list */}
      <div className="flex-1 overflow-visible">
        <div className="space-y-3 overflow-auto">
          {definitions.map((def) => {
            const validation = isDefinitionValid(def)
            return (
              <div 
                key={def.custom_definition_id} 
                className={`group bg-white border rounded-xl p-5 transition-all duration-200 hover:shadow-lg hover:border-blue-200 ${
                  !validation.valid ? 'border-red-200 bg-red-50/30' : 'border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    {/* Title and validation */}
                    <div className="flex items-center gap-3 mb-2">
                      <Typography variant="h3" color="#1F2937" fontWeight={600}>
                        {def.label || 'Untitled Metric'}
                      </Typography>
                      {!validation.valid && (
                        <div className="relative">
                          <Tooltip content={validation.reason} placement="bottom-left">
                            <div className="flex items-center justify-center w-5 h-5 bg-red-100 rounded-full">
                              <Info sx={{ fontSize: 12, color: '#DC2626' }} />
                            </div>
                          </Tooltip>
                        </div>
                      )}
                    </div>

                    {/* Description */}
                    <Typography 
                      variant="mediumParagraph" 
                      color="#6B7280"
                      className="mb-4 line-clamp-2"
                    >
                      {def.description || 'No description provided'}
                    </Typography>

                    {/* Tags - Fixed alignment and spacing */}
                    <div className="flex items-center gap-3">
                      <div className="inline-flex items-center px-3 py-1.5 text-xs font-medium bg-blue-50 text-blue-700 rounded-full border border-blue-200">
                        <Typography variant="smallParagraph" color="inherit" fontWeight={500}>
                          {formatMetricSource(def.value_source)}
                        </Typography>
                      </div>
                      <div className="inline-flex items-center px-3 py-1.5 text-xs font-medium bg-purple-50 text-purple-700 rounded-full border border-purple-200">
                        <Typography variant="smallParagraph" color="inherit" fontWeight={500}>
                          {formatMetricType(def.value_type)}
                        </Typography>
                      </div>
                    </div>
                  </div>
                  
                  {/* Action buttons - improved visibility and tooltip positioning */}
                  <div className="flex items-center gap-1 ml-4">
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          onTestMetric(def)
                        }}
                        className="flex items-center gap-1.5 px-3 py-2 text-green-600 hover:bg-green-50 rounded-lg transition-all duration-150 hover:scale-105 text-sm font-medium"
                      >
                        <PlayCircleOutline sx={{ fontSize: 16 }} />
                        <Typography variant="smallParagraph" color="inherit" fontWeight={500}>
                          Test
                        </Typography>
                      </button>
                    </div>
                    <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setEditingDef(def)
                          }}
                          className="flex items-center justify-center w-9 h-9 text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-150 hover:scale-105"
                        >
                          <Edit sx={{ fontSize: 18 }} />
                        </button>
                    </div>
                    <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDelete(def)
                          }}
                          className="flex items-center justify-center w-9 h-9 text-red-600 hover:bg-red-50 rounded-lg transition-all duration-150 hover:scale-105"
                        >
                          <Delete sx={{ fontSize: 18 }} />
                        </button>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Save changes bar - only show when there are changes */}
      {hasChanges && (
        <div className="sticky bottom-0 mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
              <Typography variant="mediumParagraph" color="#1F2937" fontWeight={500}>
                You have unsaved changes
              </Typography>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleUndoChanges}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg font-medium text-sm hover:bg-gray-50 hover:border-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
              >
                <Typography variant="mediumParagraph" color="inherit" fontWeight={500}>
                  Discard Changes
                </Typography>
              </button>
              <button
                onClick={() => handleSave(definitions)}
                disabled={definitions.some(d => !isDefinitionValid(d).valid)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shadow-sm"
                title={definitions.some(d => !isDefinitionValid(d).valid) ? 'Some metrics are invalid' : ''}
              >
                <Typography variant="mediumParagraph" color="inherit" fontWeight={500}>
                  Save Changes
                </Typography>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 